import type { Context } from '@nuxt/types';

const LOCALES = new Set(['sv', 'en']);

/**
 * Where a URL that can't be resolved ends up when no more specific pattern matches.
 * Set to `null` to keep a real 404 for anything that doesn't match a known pattern.
 */
const UNMATCHED_FALLBACK: string | null = '/';

const ANALYSES_LIST = '/alla-analyser';
const PACKAGES_LIST = '/provanalyser';
const CLINICS_LIST = '/provtagningsstallen';

/** First URL segment => closest generic page. */
const SEGMENT_FALLBACKS: Record<string, string> = {
  analys: ANALYSES_LIST,
  analyspaket: PACKAGES_LIST,
  mottagning: CLINICS_LIST,
  partner: '/',
  nyheter: '/nyheter',
  'doktorn-svarar': '/doktorn-svarar',
};

export const stripLocale = (path: string): string => {
  const [, first] = path.split('/');
  if (!LOCALES.has(first)) return path;
  return path.slice(first.length + 1) || '/';
};

const normalize = (path: string): string => `/${path.split(/[#?]/)[0].split('/').filter(Boolean).join('/')}`;

/**
 * Returns the generic page that fits the shape of an unresolvable URL, e.g.
 * /analys/unknown => /alla-analyser, /analyspaket/unknown => /provanalyser,
 * /mottagning/unknown => /provtagningsstallen.
 * Expects a path without locale prefix. Returns `null` when there is no suitable page.
 */
export const getFallbackPath = (path: string): string | null => {
  const clean = normalize(path);

  // Real files (images, scripts ...) must keep their 404; ".html" is a legacy page suffix.
  if (/\.(?!html$)[\da-z]+$/i.test(clean)) return null;

  const [first = '', second = ''] = clean.toLowerCase().split('/').filter(Boolean);

  if (SEGMENT_FALLBACKS[first]) return SEGMENT_FALLBACKS[first];

  // /<package-url>/analys[/<analysis>] with an unknown package
  if (second === 'analys') return PACKAGES_LIST;

  const value = clean.toLowerCase();
  if (/paket|package/.test(value)) return PACKAGES_LIST;
  if (/analys|analysis/.test(value)) return ANALYSES_LIST;
  if (/mottagning|provtagning|clinic|laborator/.test(value)) return CLINICS_LIST;
  if (/doktor/.test(value)) return '/doktorn-svarar';
  if (/nyheter|news/.test(value)) return '/nyheter';

  return UNMATCHED_FALLBACK;
};

/**
 * Replaces a hard 404 with a redirect to a relevant generic page.
 * Falls back to the regular 404 when there is nothing relevant or it would redirect to itself.
 * 302 on purpose: the requested page might exist again later.
 */
export const redirectToFallback = (context: Context): void => {
  const current = normalize(stripLocale(context.route.path));
  const fallback = getFallbackPath(current);

  if (!fallback || normalize(fallback) === current) {
    context.error({ statusCode: 404 });
    return;
  }

  context.redirect(302, context.app.localePath(fallback));
};
